import { Stack } from '@chakra-ui/react';
import React from 'react';

import { Badge, Typography } from 'shared';

import { WidgetCardWrapper } from 'features/chat/components/ChatMessages/ChatMessage/Widgets/CardWrapper';
import { Widget } from 'features/chat/config/types/Message';

import {
  getStatusBadgeTokens,
  getWidgetMeta,
  getWidgetPayload,
  getWidgetSubtitle,
  getWidgetTitle,
  getWidgetType,
  resolveStatusText,
  toStartCase,
  getFirstString,
  WidgetMeta,
} from './widgetMeta';

type GoalWidgetProps = {
  widget: Widget;
  meta?: WidgetMeta;
};

export const GoalWidget = ({ widget, meta }: GoalWidgetProps) => {
  const computedMeta = meta ?? getWidgetMeta(widget);
  const title =
    getWidgetTitle(widget, computedMeta) ??
    toStartCase(computedMeta.statusLabel);
  const subtitle = getWidgetSubtitle(widget, computedMeta);
  const statusTokens = getStatusBadgeTokens(computedMeta.statusVariant);
  const statusText = resolveStatusText(widget, computedMeta);
  const details = buildGoalDetails(widget, subtitle);

  return (
    <WidgetCardWrapper>
      <Stack gap="12px" alignItems="flex-start">
        <Badge colorPalette="lightGray">{computedMeta.entityLabel}</Badge>

        <Stack gap="6px" width="100%">
          <Typography type="meta" lineHeight="normal">
            {title}
          </Typography>

          {details.map((detail) => (
            <Typography
              key={detail}
              fontSize="14px"
              fontWeight="400"
              color="gray"
              lineHeight="normal"
            >
              {detail}
            </Typography>
          ))}
        </Stack>

        <Badge
          bg={statusTokens.bg}
          color={statusTokens.color}
          borderRadius="12px"
        >
          {statusText}
        </Badge>
      </Stack>
    </WidgetCardWrapper>
  );
};

const buildGoalDetails = (widget: Widget, subtitle?: string): string[] => {
  const details = new Set<string>();

  if (subtitle) {
    details.add(subtitle);
  }

  const payload = getWidgetPayload(widget);
  const widgetType = getWidgetType(widget);

  if (widgetType === 'goalMoved') {
    const destination = getFirstString(payload, [
      'to_period',
      'toPeriod',
      'to',
      'destination',
    ]);

    if (destination) {
      details.add(`Moved to ${toStartCase(destination)}`);
    }
  }

  if (widgetType === 'goalPrioritized') {
    const priority = getFirstString(payload, ['priority', 'rank']);

    if (priority) {
      details.add(`Priority ${toStartCase(priority)}`);
    }
  }

  if (widgetType === 'goalUnprioritized') {
    const reason = getFirstString(payload, ['reason']);

    if (reason) {
      details.add(toStartCase(reason));
    }
  }

  return Array.from(details);
};
